import * as React from 'react';

type WindowWithTypekit = Window & {
  Typekit?: {
    load: (args: {async: boolean}) => void;
  };
};

type Props = {
  src: string;
  onLoad?: (arg1: WindowWithTypekit, arg2: string) => void;
  /**
   * Dynamic scripts behave as “async” by default. This can be changed if we explicitly set script.async=false.
   * Then scripts will be executed in the document order, just like defer. See more: https://javascript.info/script-async-defer#dynamic-scripts
   */
  async?: boolean;
};

export default class Script extends React.PureComponent<Props> {
  script: HTMLScriptElement | null = null;
  document = null;
  window = null;
  element = React.createRef<HTMLElement>();

  componentDidUpdate(prevProps: Props) {
    if (this.props.src !== prevProps.src) {
      this.loadScript();
    }
  }

  componentDidMount() {
    this.loadScript();
  }

  render() {
    // Noscript is a do-nothing element, and is allowed pretty much anywhere
    // on the page (most elements are not allowed in a <head> element).
    return <noscript ref={this.element} />;
  }

  componentWillUnmount() {
    this.removeScript();
  }

  removeScript() {
    if (this.script) {
      this.script.onload = null;
      this.script.remove();
      this.script = null;
    }
  }

  loadScript() {
    this.removeScript();
    const element = this.element.current;
    if (element === null) {
      return;
    }
    const script = element.ownerDocument.createElement('script');
    script.type = 'text/javascript';
    const {src, onLoad, async} = this.props;
    if (onLoad) {
      const window = element.ownerDocument.defaultView;

      if (!window) return;

      script.onload = () => {
        onLoad(window, src);
      };
    }
    script.src = src;
    if (async) script.async = async;
    element.parentNode?.insertBefore(script, element);
    this.script = script;
  }
}
